import { useState, useEffect } from 'react';
import { LineChartOutlined, ThunderboltOutlined, AppstoreOutlined, SearchOutlined } from '@ant-design/icons';
import * as api from '../../services/api';
import './Results.css';

type FilterType = 'exercise' | 'daily' | 'bodypart';

interface ChartData {
  label: string;
  points: number;
}

function SimpleLineChart({ data, title }: { data: ChartData[]; title: string }) {
  if (data.length === 0) {
    return (
      <div className="chart-empty">
        <p>No data yet</p>
      </div>
    );
  }

  const maxPoints = Math.max(...data.map(d => d.points), 1);
  const minPoints = Math.min(...data.map(d => d.points));
  const range = maxPoints - minPoints || 1;

  // SVG dimensions
  const width = 320;
  const height = 180;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Calculate points
  const points = data.map((d, i) => ({
    x: padding.left + (i / (data.length - 1 || 1)) * chartWidth,
    y: padding.top + chartHeight - ((d.points - minPoints) / range) * chartHeight,
    ...d,
  }));

  // Create path
  const linePath = points.map((p, i) =>
    `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`
  ).join(' ');

  // Area path for gradient fill
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${padding.top + chartHeight} L ${points[0].x} ${padding.top + chartHeight} Z`;

  return (
    <div className="chart-container">
      <h4 className="chart-title">{title}</h4>
      <svg viewBox={`0 0 ${width} ${height}`} className="line-chart">
        <defs>
          <linearGradient id={`gradient-${title.replace(/\s/g, '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
          <line
            key={i}
            x1={padding.left}
            y1={padding.top + chartHeight * (1 - ratio)}
            x2={width - padding.right}
            y2={padding.top + chartHeight * (1 - ratio)}
            stroke="#333"
            strokeWidth="1"
            strokeDasharray="4,4"
          />
        ))}

        {/* Y-axis labels */}
        {[0, 0.5, 1].map((ratio, i) => (
          <text
            key={i}
            x={padding.left - 8}
            y={padding.top + chartHeight * (1 - ratio) + 4}
            textAnchor="end"
            fontSize="9"
            fill="#666"
          >
            {Math.round(minPoints + range * ratio)}
          </text>
        ))}

        {/* Area fill */}
        <path d={areaPath} fill={`url(#gradient-${title.replace(/\s/g, '')})`} />

        {/* Line */}
        <path
          d={linePath}
          fill="none"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Data points */}
        {points.map((p, i) => (
          <g key={i}>
            <circle
              cx={p.x}
              cy={p.y}
              r="3"
              fill="#1a1a1a"
              stroke="#fff"
              strokeWidth="2"
            />
            {/* X-axis label */}
            <text
              x={p.x}
              y={height - 8}
              textAnchor="middle"
              fontSize="8"
              fill="#666"
              transform={`rotate(-45, ${p.x}, ${height - 8})`}
            >
              {p.label}
            </text>
          </g>
        ))}
      </svg>
      <div className="chart-stats">
        <span className="stat">
          Latest: <strong>{data[data.length - 1]?.points.toLocaleString() || 0}</strong> pts
        </span>
        {data.length > 1 && (
          <span className={`stat trend ${data[data.length - 1].points >= data[0].points ? 'up' : 'down'}`}>
            {data[data.length - 1].points >= data[0].points ? '↑' : '↓'}
            {Math.abs(data[data.length - 1].points - data[0].points).toLocaleString()} pts
          </span>
        )}
      </div>
    </div>
  );
}

export function Results() {
  const [filter, setFilter] = useState<FilterType>('exercise');
  const [dailyData, setDailyData] = useState<api.DailyTrendPoint[]>([]);
  const [exerciseData, setExerciseData] = useState<api.ExerciseTrend[]>([]);
  const [bodyPartData, setBodyPartData] = useState<api.BodyPartTrend[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [daily, exercise, bodypart] = await Promise.all([
          api.getDailyTrend(),
          api.getExerciseTrend(),
          api.getBodyPartTrend(),
        ]);
        setDailyData(daily);
        setExerciseData(exercise);
        setBodyPartData(bodypart);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  const renderCharts = () => {
    if (loading) {
      return <div className="loading">Loading...</div>;
    }

    const normalizedSearch = search.trim().toLowerCase();

    switch (filter) {
      case 'daily':
        return (
          <div className="charts-grid">
            <SimpleLineChart
              title="Daily Points"
              data={dailyData.map(d => ({
                label: formatDate(d.date),
                points: d.points,
              }))}
            />
          </div>
        );

      case 'exercise':
        return (
          <div className="charts-grid">
            {exerciseData.length === 0 ? (
              <div className="empty-state">
                <p>No exercise data yet</p>
                <span>Complete some workouts to see your progress</span>
              </div>
            ) : (
              exerciseData
                .filter(ex =>
                  !normalizedSearch ||
                  ex.name.toLowerCase().includes(normalizedSearch) ||
                  ex.bodyParts.some(bp => bp.toLowerCase().includes(normalizedSearch))
                )
                .map(ex => (
                  <SimpleLineChart
                    key={ex.name}
                    title={ex.name}
                    data={ex.sessions.map(s => ({
                      label: formatDate(s.date),
                      points: s.points,
                    }))}
                  />
                ))
            )}
          </div>
        );

      case 'bodypart':
        return (
          <div className="charts-grid">
            {bodyPartData.length === 0 ? (
              <div className="empty-state">
                <p>No body part data yet</p>
                <span>Add body parts to your workouts to track progress</span>
              </div>
            ) : (
              bodyPartData
                .filter(bp =>
                  !normalizedSearch || bp.bodyPart.toLowerCase().includes(normalizedSearch)
                )
                .map(bp => (
                  <SimpleLineChart
                    key={bp.bodyPart}
                    title={bp.bodyPart}
                    data={bp.sessions.map(s => ({
                      label: formatDate(s.date),
                      points: s.points,
                    }))}
                  />
                ))
            )}
          </div>
        );
    }
  };

  return (
    <div className="results-page">
      <div className="results-header">
        <h1>Results</h1>
        <p className="results-subtitle">Track your progress over time</p>
      </div>

      <div className="filter-tabs">
        <button
          className={`filter-tab ${filter === 'exercise' ? 'active' : ''}`}
          onClick={() => setFilter('exercise')}
        >
          <ThunderboltOutlined />
          <span>Exercise</span>
        </button>
        <button
          className={`filter-tab ${filter === 'daily' ? 'active' : ''}`}
          onClick={() => setFilter('daily')}
        >
          <LineChartOutlined />
          <span>Daily</span>
        </button>
        <button
          className={`filter-tab ${filter === 'bodypart' ? 'active' : ''}`}
          onClick={() => setFilter('bodypart')}
        >
          <AppstoreOutlined />
          <span>Body Part</span>
        </button>
      </div>

      {(filter === 'exercise' || filter === 'bodypart') && (
        <div className="results-search">
          <SearchOutlined />
          <input
            type="text"
            placeholder={
              filter === 'exercise'
                ? 'Search exercises or related body parts'
                : 'Search body parts'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="clear-btn" onClick={() => setSearch('')}>
              Clear
            </button>
          )}
        </div>
      )}

      <div className="results-content">
        {renderCharts()}
      </div>
    </div>
  );
}
